import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNotifications } from '../contexts/NotificationContext'
import { useTranslation } from '../hooks/useTranslation'
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion'
import { useNotificationSwipe } from '../hooks/useNotificationSwipe'
import {
  ClearAllGlyph,
  EmptyBellGlyph,
  MarkReadGlyph,
  NotificationKindIcon,
  TrashGlyph,
} from './NotificationIcons'
import {
  CLEAR_CARD_DURATION,
  clearAllDuration,
  clearStaggerDelay,
  formatNotificationDate,
  hasNotificationCopy,
  idsToClear,
  isStaleNotification,
  notificationKind,
  sortNotificationsByDate,
  unreadLabel,
} from '../utils/notificationPolicy'
import '../styles/scrollbar.css'
import './NotificationMenu.css'

const MENU_WIDTH = 380
const EDGE_PADDING = 8

function anchorToBell(bell) {
  if (!bell) return { top: 0, left: 0, origin: '90% 0' }
  const rect = bell.getBoundingClientRect()
  const maxLeft = Math.max(EDGE_PADDING, window.innerWidth - MENU_WIDTH - EDGE_PADDING)
  const left = Math.min(Math.max(EDGE_PADDING, rect.right - MENU_WIDTH), maxLeft)
  const estimatedHeight = Math.min(440, window.innerHeight * 0.7)
  const maxTop = Math.max(EDGE_PADDING, window.innerHeight - estimatedHeight - EDGE_PADDING)
  return {
    top: Math.min(rect.bottom + EDGE_PADDING, maxTop),
    left,
    origin: `${Math.round(rect.left + rect.width / 2 - left)}px 0`,
  }
}

function NotificationMenuItem({
  notification,
  now,
  clearing,
  delay,
  reducedMotion,
  onMarkRead,
  onRemove,
}) {
  const { t } = useTranslation()
  const swipe = useNotificationSwipe({
    enabled: !clearing,
    reducedMotion,
    onDismiss: () => onRemove(notification.id),
  })
  const wasClearing = useRef(false)
  useEffect(() => {
    if (wasClearing.current && !clearing) swipe.reset()
    wasClearing.current = clearing
  }, [clearing, swipe.reset])
  const kind = notificationKind(notification.type)
  const stale = isStaleNotification(notification.timestamp, now)

  return (
    <div
      className={`notification-menu__slot${clearing ? ' is-clearing' : ''}`}
      style={clearing && !reducedMotion ? { transitionDelay: `${delay}ms` } : undefined}
    >
      <div
        ref={swipe.nodeRef}
        className={`notification-menu__swipe ${swipe.className}`}
        style={swipe.style}
      >
        <article
          className={`notification-menu__item is-${kind}${notification.read ? ' is-read' : ''}${stale ? ' is-stale' : ''}${clearing ? ' is-clearing' : ''}${swipe.exiting ? ' is-swipe-exit' : ''}`}
          style={clearing && !reducedMotion ? {
            animationDuration: `${CLEAR_CARD_DURATION}ms`,
            animationDelay: `${delay}ms`,
          } : undefined}
        >
          <span className="notification-menu__item-icon">
            <NotificationKindIcon kind={kind} size={18} />
          </span>
          <div className="notification-menu__item-content">
            <h4>{notification.title}</h4>
            <p>{notification.message}</p>
            <p className="notification-menu__item-date">
              {formatNotificationDate(notification.timestamp, now)}
              {stale ? <span className="notification-menu__item-stale">alerte passée</span> : null}
            </p>
          </div>
          <div className="notification-menu__item-tools">
            {notification.read ? null : (
              <button
                type="button"
                className="notification-menu__item-read"
                onClick={() => onMarkRead(notification.id)}
                title="Marquer comme lue"
                aria-label={`Marquer comme lue : ${notification.title}`}
              >
                <span className="notification-menu__item-unread" aria-hidden="true" />
              </button>
            )}
            <button
              type="button"
              className="notification-menu__item-delete"
              onClick={() => onRemove(notification.id)}
              title={t('dismiss') || 'Supprimer'}
              aria-label={`Supprimer la notification : ${notification.title}`}
            >
              <TrashGlyph />
            </button>
          </div>
        </article>
      </div>
    </div>
  )
}

const NotificationMenu = ({ bellRef, isClosing = false, onRequestClose, onClosed }) => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearByIds,
    restoreNotifications,
    addNotification,
  } = useNotifications()
  const { t } = useTranslation()
  const reducedMotion = usePrefersReducedMotion()
  const menuRef = useRef(null)
  const [anchor, setAnchor] = useState(() => anchorToBell(bellRef?.current))
  const [isPositioned, setIsPositioned] = useState(false)
  const [clearingIds, setClearingIds] = useState(null)
  const [bulkClearing, setBulkClearing] = useState(false)
  const clearingRef = useRef(false)
  const pendingIdsRef = useRef(new Set())
  const now = useRef(new Date()).current

  const requestClose = useCallback(() => {
    onRequestClose?.()
  }, [onRequestClose])

  useEffect(() => {
    const updatePosition = () => {
      setAnchor(anchorToBell(bellRef?.current))
      setIsPositioned(true)
    }

    updatePosition()
    const frame = requestAnimationFrame(updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    const handleClickOutside = (event) => {
      if (
        menuRef.current && !menuRef.current.contains(event.target)
        && bellRef?.current && !bellRef.current.contains(event.target)
      ) {
        requestClose()
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') requestClose()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [bellRef, requestClose])

  useEffect(() => {
    if (!isClosing) return undefined
    if (!reducedMotion) return undefined
    onClosed?.()
    return undefined
  }, [isClosing, reducedMotion, onClosed])

  const releasePending = useCallback((id) => {
    pendingIdsRef.current.delete(id)
    setClearingIds((previous) => {
      if (!previous) return null
      const next = new Set(previous)
      next.delete(id)
      return next.size ? next : null
    })
  }, [])

  const removeMenuItem = useCallback(async (id) => {
    if (!id || pendingIdsRef.current.has(id) || clearingRef.current) return
    pendingIdsRef.current.add(id)
    const snapshot = sortNotificationsByDate(notifications).filter((item) => item.id === id)
    setClearingIds((previous) => new Set([...(previous || []), id]))
    try {
      const wait = reducedMotion ? 0 : CLEAR_CARD_DURATION
      if (wait) {
        await new Promise((resolve) => window.setTimeout(resolve, wait + 40))
      }
      removeNotification(id)
      releasePending(id)
    } catch (error) {
      releasePending(id)
      restoreNotifications(snapshot)
      addNotification({
        type: 'error',
        title: t('error') || 'Erreur',
        message: 'Impossible de supprimer la notification.',
        showToast: true,
      })
    }
  }, [addNotification, notifications, reducedMotion, releasePending, removeNotification, restoreNotifications, t])

  const phaseClass = isClosing ? 'is-closing' : (isPositioned ? 'is-open' : '')
  const sorted = sortNotificationsByDate(notifications).filter(hasNotificationCopy)
  const leavingIds = clearingIds
  const displayNotifications = bulkClearing && leavingIds
    ? [
        ...sorted.filter((item) => !leavingIds.has(item.id)),
        ...sorted.filter((item) => leavingIds.has(item.id)),
      ]
    : sorted

  const handleClear = useCallback(async () => {
    if (clearingRef.current || !notifications.length) return
    clearingRef.current = true
    setBulkClearing(true)
    const snapshot = sortNotificationsByDate(notifications)
    const ids = idsToClear(snapshot)
    setClearingIds(new Set(ids))
    try {
      const wait = reducedMotion ? 0 : clearAllDuration(ids.length)
      if (wait) {
        await new Promise((resolve) => window.setTimeout(resolve, wait + 40))
      }
      clearByIds(ids)
      setClearingIds(null)
    } catch (error) {
      setClearingIds(null)
      restoreNotifications(snapshot)
      addNotification({
        type: 'error',
        title: t('error') || 'Erreur',
        message: 'Impossible d’effacer les notifications.',
        showToast: true,
      })
    } finally {
      setBulkClearing(false)
      clearingRef.current = false
    }
  }, [addNotification, clearByIds, notifications, reducedMotion, restoreNotifications, t])

  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      ref={menuRef}
      className={`notification-menu ${phaseClass}`.trim()}
      style={{
        top: `${anchor.top}px`,
        left: `${anchor.left}px`,
        transformOrigin: anchor.origin,
      }}
      role="dialog"
      aria-label={t('notifications') || 'Notifications'}
      onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget) return
        if (isClosing) onClosed?.()
      }}
    >
      <div className="notification-menu__header">
        <div className="notification-menu__heading">
          <h3>{t('notifications') || 'Notifications'}</h3>
          <p className="notification-menu__count">{unreadLabel(unreadCount)}</p>
        </div>
        {notifications.length > 0 ? (
          <div className="notification-menu__actions">
            <button
              type="button"
              className="notification-menu__action-btn"
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              title={t('markAllAsRead') || 'Tout marquer comme lu'}
              aria-label={t('markAllAsRead') || 'Tout marquer comme lu'}
            >
              <MarkReadGlyph />
            </button>
            <button
              type="button"
              className="notification-menu__action-btn"
              onClick={handleClear}
              disabled={bulkClearing}
              title={t('clearAll') || 'Tout effacer'}
              aria-label={t('clearAll') || 'Tout effacer'}
            >
              <ClearAllGlyph />
            </button>
          </div>
        ) : null}
      </div>

      <div className="notification-menu__list lg-scroll">
        {displayNotifications.length === 0 ? (
          <div className="notification-menu__empty">
            <EmptyBellGlyph />
            <p>{t('noNotifications') || 'Aucune notification'}</p>
            <p className="notification-menu__empty-hint">Les alertes de prix et de budget arrivent ici.</p>
          </div>
        ) : (
          displayNotifications.map((notification) => {
            const clearing = Boolean(leavingIds?.has(notification.id))
            const clearingIndex = clearing
              ? displayNotifications.filter((item) => leavingIds.has(item.id)).findIndex((item) => item.id === notification.id)
              : -1
            const delay = clearing && bulkClearing && !reducedMotion
              ? clearStaggerDelay(Math.max(0, clearingIndex), leavingIds.size)
              : 0
            return (
              <NotificationMenuItem
                key={notification.id}
                notification={notification}
                now={now}
                clearing={clearing}
                delay={delay}
                reducedMotion={reducedMotion}
                onMarkRead={markAsRead}
                onRemove={removeMenuItem}
              />
            )
          })
        )}
      </div>
    </div>,
    document.body,
  )
}

export default NotificationMenu
